// frontend/src/services/notificationService.js
/**
 * Notification Service
 * 
 * Provides notification API calls for the HR Portal.
 * Replaces the previous mock implementation with real backend integration.
 */

import { staffAPI } from './api';

export const notificationService = {
  /**
   * Get all notifications for the current user
   * @param {Object} params - Query parameters (category, is_read, limit, etc.)
   * @returns {Promise<Object>} - { results: [], unread_count: number, total: number }
   */
  getNotifications: async (params = {}) => {
    try {
      const response = await staffAPI.getNotifications(params);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      throw error;
    }
  },

  /**
   * Get unread notification count (lightweight for polling)
   * @returns {Promise<Object>} - { unread_count: number, recent_unread_ids: [] }
   */
  getUnreadCount: async () => {
    try {
      const response = await staffAPI.getUnreadNotificationCount();
      return response.data;
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      throw error;
    }
  },

  /**
   * Mark a single notification as read
   * @param {string} notificationId - UUID of the notification
   * @returns {Promise<Object>} - { success: boolean, notification: {}, unread_count: number }
   */
  markAsRead: async (notificationId) => {
    try {
      const response = await staffAPI.markNotificationAsRead(notificationId);
      return response.data;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read
   * @param {string|null} category - Optional category filter
   * @returns {Promise<Object>} - { success: boolean, marked_count: number, unread_count: number }
   */
  markAllAsRead: async (category = null) => {
    try {
      const response = await staffAPI.markAllNotificationsAsRead(category);
      return response.data;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      throw error;
    }
  },

  /**
   * Mark a notification as resolved (action completed)
   * @param {string} notificationId - UUID of the notification
   * @returns {Promise<Object>} - { success: boolean, notification: {} }
   */
  markAsResolved: async (notificationId) => {
    try {
      const response = await staffAPI.markNotificationResolved(notificationId);
      return response.data;
    } catch (error) {
      console.error('Failed to mark notification as resolved:', error);
      throw error;
    }
  },

  /**
   * Get notification counts grouped by category
   * @returns {Promise<Object>} - { categories: [], total_unread: number }
   */
  getByCategory: async () => {
    try {
      const response = await staffAPI.getNotificationsByCategory();
      return response.data;
    } catch (error) {
      console.error('Failed to fetch notifications by category:', error);
      throw error;
    }
  },
};

export default notificationService;
